import { Link } from 'react-router-dom';

const Terms = () => {
  return (
    <div className="min-h-screen bg-background text-foreground font-body overflow-auto fixed inset-0">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <header className="mb-8">
          <Link
            to="/"
            className="text-2xl sm:text-3xl font-display font-bold text-primary neon-text hover:opacity-80 transition-opacity"
          >
            SPACASNAKE<span className="text-secondary neon-text-magenta">.IO</span>
          </Link>
        </header>

        <h1 className="text-2xl sm:text-3xl font-display font-bold text-primary mb-6">
          Terms &amp; Conditions
        </h1>
        <p className="text-muted-foreground text-sm mb-8">
          Last updated: February 8, 2025
        </p>

        <div className="space-y-6 text-sm sm:text-base text-muted-foreground leading-relaxed">
          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing and using SpacaSnake.io (the "Service"), you agree to
              be bound by these Terms &amp; Conditions. If you do not agree with
              any part of these terms, you must not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              2. Description of Service
            </h2>
            <p>
              SpacaSnake.io is a free-to-play browser-based 3D snake game. The
              Service is provided "as is" and "as available" without any
              warranties of any kind. We reserve the right to modify, suspend,
              or discontinue the Service at any time without notice.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              3. Use of the Service
            </h2>
            <p className="mb-2">You agree to use the Service only for lawful purposes. You must not:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Use the Service in any way that violates applicable laws or regulations.</li>
              <li>Attempt to interfere with or disrupt the Service or its servers.</li>
              <li>Use automated scripts, bots, or other tools to interact with the Service without authorization.</li>
              <li>Attempt to gain unauthorized access to any part of the Service.</li>
              <li>Harass, abuse, or harm other users.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              4. Intellectual Property
            </h2>
            <p>
              All content, graphics, code, and assets on SpacaSnake.io are the
              property of SpacaSnake or its licensors and are protected by
              intellectual property laws. You may not reproduce, distribute,
              modify, or create derivative works from any part of the Service
              without our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              5. User Content
            </h2>
            <p>
              Any player names or content you submit through the Service must
              not be offensive, obscene, or infringe upon the rights of others.
              We reserve the right to remove any user content that violates
              these terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              6. Advertisements
            </h2>
            <p>
              The Service may display advertisements provided by third-party ad
              networks, including Google AdSense. These ads may use cookies and
              similar technologies. By using the Service, you acknowledge that
              advertisements may be displayed. For more details, see our{' '}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              7. Disclaimer of Warranties
            </h2>
            <p>
              The Service is provided on an "as is" and "as available" basis. We
              make no warranties, express or implied, regarding the Service,
              including but not limited to warranties of merchantability, fitness
              for a particular purpose, or non-infringement. We do not warrant
              that the Service will be uninterrupted, error-free, or secure.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              8. Limitation of Liability
            </h2>
            <p>
              To the fullest extent permitted by law, SpacaSnake and its
              operators shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages arising from your use
              of the Service, even if we have been advised of the possibility of
              such damages. Our total liability for any claims arising from your
              use of the Service shall not exceed the amount you have paid to us
              (if any).
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              9. Indemnification
            </h2>
            <p>
              You agree to indemnify and hold harmless SpacaSnake and its
              operators from any claims, losses, damages, liabilities, and
              expenses arising from your use of the Service or violation of
              these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              10. Changes to These Terms
            </h2>
            <p>
              We may update these Terms &amp; Conditions from time to time.
              Changes will be posted on this page with an updated "Last updated"
              date. Your continued use of the Service after changes constitutes
              acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              11. Governing Law
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with
              applicable laws, without regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-3">
              12. Contact Us
            </h2>
            <p>
              If you have questions about these Terms &amp; Conditions, please
              contact us at{' '}
              <a
                href="mailto:contact@spacasnake.io"
                className="text-primary hover:underline"
              >
                contact@spacasnake.io
              </a>
              .
            </p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10">
          <Link
            to="/"
            className="text-primary hover:underline text-sm font-display"
          >
            &larr; Back to game
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
